"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = useIdleSession;
exports.useContextLabel = useContextLabel;
exports.useDraftSaver = useDraftSaver;
var _react = require("react");
var _idleClock = require("./idleClock");
/**
 * useIdleSession — wires an idle clock into a React app.
 *
 * The clock decides *when*. This hook decides *what the screen does about it*,
 * and calls back once when the session has to end.
 *
 *   const idle = useIdleSession({ clock, enabled: isAuthenticated, onLock });
 *
 * `onLock` is called once per lock, with `{ owned, reason }`. `owned` is true
 * in exactly one tab: the one that got there first. Only that tab should talk
 * to the server. It is called *after* any draft save has finished, because
 * revoking the token while a save is in flight makes that save fail with a 401
 * — which is the exact thing this feature exists to prevent.
 *
 * The hook never signs anybody out itself and never touches the network. That
 * keeps the lock working when the network is down.
 */
function useIdleSession({
  clock,
  enabled = true,
  onLock
} = {}) {
  const [state, setState] = (0, _react.useState)(() => ({
    phase: _idleClock.PHASE_ACTIVE,
    reason: '',
    idleMs: 0,
    msUntilLock: 0,
    saving: false,
    saveResults: [],
    contextLabel: null
  }));

  // onLock is read through a ref so a caller that rebuilds the function on
  // every render does not restart the clock.
  const onLockRef = (0, _react.useRef)(onLock);
  (0, _react.useEffect)(() => {
    onLockRef.current = onLock;
  }, [onLock]);

  // The lock must fire once, not once per tick.
  const lockedRef = (0, _react.useRef)(false);
  (0, _react.useEffect)(() => {
    if (!clock || !enabled) return undefined;
    const unsubscribe = clock.subscribe(next => {
      setState(next);
      if (next.phase !== _idleClock.PHASE_LOCKED) {
        lockedRef.current = false;
        return;
      }
      if (lockedRef.current) return;
      lockedRef.current = true;
      // lockNow claims the sign-out, runs any draft savers that have not
      // run yet, and waits for them within a time limit.
      Promise.resolve(clock.lockNow(next.reason || _idleClock.END_REASON_IDLE)).then(({
        owned
      }) => {
        const handler = onLockRef.current;
        if (handler) return handler({
          owned,
          reason: next.reason || _idleClock.END_REASON_IDLE
        });
        return undefined;
      }).catch(() => {
        // A failed sign-out must not un-lock the screen. The tab is
        // already showing the lock, and the server backstop covers
        // the token.
      });
    });
    clock.start();
    return () => {
      unsubscribe();
      clock.stop();
    };
  }, [clock, enabled]);
  const staySignedIn = (0, _react.useCallback)(() => {
    if (clock) clock.staySignedIn();
  }, [clock]);
  const registerDraftSaver = (0, _react.useCallback)(saver => {
    if (!clock) return () => {};
    return clock.registerDraftSaver(saver);
  }, [clock]);
  const setContextLabel = (0, _react.useCallback)(label => {
    if (clock) clock.setContextLabel(label);
  }, [clock]);
  return {
    phase: state.phase,
    isWarning: state.phase === _idleClock.PHASE_WARNING,
    isLocked: state.phase === _idleClock.PHASE_LOCKED,
    reason: state.reason,
    msUntilLock: state.msUntilLock,
    secondsUntilLock: Math.ceil((state.msUntilLock || 0) / 1000),
    saving: state.saving,
    saveResults: state.saveResults || [],
    contextLabel: state.contextLabel || null,
    staySignedIn,
    registerDraftSaver,
    setContextLabel
  };
}

/**
 * useDraftSaver — register one panel's unsaved work with the idle clock.
 *
 *   useDraftSaver(idle, () => {
 *     if (!isDirty) return undefined;             // nothing to rescue
 *     const ok = store.saveDraft({ ... });
 *     return { saved: ok, where: 'local', label: surveyName };
 *   }, [isDirty, surveyName]);
 *
 * Returning nothing means "I had nothing unsaved", which is different from
 * "I tried and failed". The lock screen tells those apart, so do not return a
 * fake success.
 */
function useDraftSaver(idle, saver, deps = []) {
  const saverRef = (0, _react.useRef)(saver);
  (0, _react.useEffect)(() => {
    saverRef.current = saver;
  });
  const register = idle && idle.registerDraftSaver;
  (0, _react.useEffect)(() => {
    if (!register) return undefined;
    // The registered function reads through the ref, so the panel can
    // re-render freely without unregistering and re-registering.
    return register((...args) => saverRef.current(...args));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, ...deps]);
}

/**
 * useContextLabel — name what this tab currently has open.
 *
 *   useContextLabel(idle, surveyData?.title);
 *
 * Keeps the idle clock's context label current as `label` changes. When this
 * tab locks, its own lock screen shows the last value here — the survey
 * title, the channel being set up, whatever was passed last — instead of a
 * blank "you were signed out". Pass `null` (or nothing) when there is nothing
 * to name; the lock screen falls back to its default text.
 */
function useContextLabel(idle, label) {
  const setLabel = idle && idle.setContextLabel;
  (0, _react.useEffect)(() => {
    if (!setLabel) return undefined;
    setLabel(label || null);
    // Do not clear on unmount: the app is unmounting *because* it locked,
    // and the point of this hook is that the label survives that moment.
    return undefined;
  }, [setLabel, label]);
}